import React, { useState, useEffect } from "react";
import { Mail, Lock, User, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../hooks/useAuth";
import Modal from "../components/_ResponsiveComponents/Modal";

const Auth: React.FC = () => {
  const [isTermsOpen, setTermsOpen] = useState(false);
  const [isPrivacyOpen, setPrivacyOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState("signin");
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    if (!email || !password) {
      setMessage({ type: "error", text: "Please fill in all fields" });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        setMessage({ type: "error", text: error.message });
      } else {
        setMessage({ type: "success", text: "Successfully signed in!" });
        setTimeout(() => navigate("/"), 1000);
      }
    } catch (error) {
      setMessage({ type: "error", text: "An unexpected error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const firstName = formData.get("firstName") as string;
    const lastName = formData.get("lastName") as string;
    const email = formData.get("email") as string;
    const phone = formData.get("phone") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;

    if (
      !firstName ||
      !lastName ||
      !email ||
      !phone ||
      !password ||
      !confirmPassword
    ) {
      setMessage({ type: "error", text: "Please fill in all fields" });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({ type: "error", text: "Passwords do not match" });
      return;
    }

    if (password.length < 6) {
      setMessage({
        type: "error",
        text: "Password must be at least 6 characters",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            phone: phone,
            full_name: `${firstName} ${lastName}`,
          },
        },
      });

      if (error) {
        setMessage({ type: "error", text: error.message });
      } else if (data.user && !data.user.email_confirmed_at) {
        setMessage({
          type: "success",
          text: "Please check your email to confirm your account!",
        });
      } else {
        setMessage({ type: "success", text: "Account created successfully!" });
        setTimeout(() => navigate("/"), 1000);
      }
    } catch (error) {
      setMessage({ type: "error", text: "An unexpected error occurred" });
    } finally {
      setLoading(false);
    }
  };

  // const handleGoogleSignIn = async () => {
  //   setLoading(true);
  //   try {
  //     const { error } = await supabase.auth.signInWithOAuth({
  //       provider: "google",
  //       options: {
  //         redirectTo: `${window.location.origin}/`,
  //       },
  //     });
  //     if (error) {
  //       setMessage({ type: "error", text: error.message });
  //     } else {
  //       // OAuth redirect will handle the navigation
  //       setMessage({
  //         type: "success",
  //         text: "Redirecting to Google...",
  //       });
  //     }
  //   } catch (error) {
  //     setMessage({ type: "error", text: "An unexpected error occurred" });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const SignInPage = () => (
    <div className="min-h-screen bg-linear-to-br from-neutral-900 via-neutral-900 to-neutral-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-neutral-800 rounded-2xl shadow-xl border border-neutral-700 overflow-hidden">
          {/* Logo Section */}
          <div className="bg-neutral-800 p-8 text-center border-b border-neutral-700">
            <img
              src="/assets/Images/logo/blackstore.png"
              alt="BlackStore"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
            <p className="text-neutral-400">Sign in to your account</p>
          </div>

          <div className="p-8">
            {message.text && (
              <div
                className={`mb-6 p-4 rounded-lg text-sm font-medium ${
                  message.type === "error"
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-green-50 text-green-700 border border-green-200"
                }`}
              >
                {message.text}
              </div>
            )}

            <form onSubmit={handleSignIn} className="space-y-6">
              <div>
                <label
                  htmlFor="signin-email"
                  className="block text-sm font-medium text-neutral-300 mb-2"
                >
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 w-5 h-5" />
                  <input
                    id="signin-email"
                    name="email"
                    type="email"
                    required
                    className="w-full pl-12 pr-4 py-3 border border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-neutral-900 text-white placeholder-neutral-500 focus:bg-neutral-900"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="signin-password"
                  className="block text-sm font-medium text-neutral-300 mb-2"
                >
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 w-5 h-5" />
                  <input
                    id="signin-password"
                    name="password"
                    type={showSignInPassword ? "text" : "password"}
                    required
                    className="w-full pl-12 pr-12 py-3 border border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-neutral-900 text-white placeholder-neutral-500 focus:bg-neutral-900"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowSignInPassword(!showSignInPassword);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
                  >
                    {/* {showSignInPassword ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <EyeOff className="w-5 h-5" />
                    )} */}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-primary border-neutral-700 rounded focus:ring-primary bg-neutral-900"
                  />
                  <span className="ml-2 text-sm text-neutral-400">
                    Remember me
                  </span>
                </label>
                <a
                  href="/forgot-password"
                  className="text-sm text-primary hover:text-primary-light font-medium"
                >
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-linear-to-r from-primary to-primary-dark text-black py-3 rounded-lg font-semibold hover:from-primary-light hover:to-primary focus:ring-4 focus:ring-primary/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <p className="mt-8 text-center text-sm text-neutral-400">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => setCurrentPage("signup")}
                className="text-primary hover:text-primary-light font-medium"
              >
                Sign up
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const SignUpPage = () => (
    <div className="min-h-screen bg-linear-to-br from-neutral-900 via-neutral-900 to-neutral-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-neutral-800 rounded-2xl shadow-xl border border-neutral-700 overflow-hidden">
          <div className="bg-neutral-800 p-8 text-center border-b border-neutral-700">
            <img
              src="/assets/Images/logo/blackstore.png"
              alt="BlackStore"
              className="mx-auto h-16 w-auto mb-4"
            />
            <h2 className="text-3xl font-bold text-white mb-2">
              Create Account
            </h2>
            <p className="text-neutral-400">Join us today</p>
          </div>

          <div className="p-8">
            {message.text && (
              <div
                className={`mb-6 p-4 rounded-lg text-sm font-medium ${
                  message.type === "error"
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-green-50 text-green-700 border border-green-200"
                }`}
              >
                {message.text}
              </div>
            )}

            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="signup-firstName"
                    className="block text-sm font-medium text-neutral-300 mb-1"
                  >
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 w-4 h-4" />
                    <input
                      id="signup-firstName"
                      name="firstName"
                      type="text"
                      required
                      className="w-full pl-10 pr-3 py-2.5 border border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-neutral-900 text-white placeholder-neutral-500 focus:bg-neutral-900 text-sm"
                      placeholder="First name"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="signup-lastName"
                    className="block text-sm font-medium text-neutral-300 mb-1"
                  >
                    Last Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 w-4 h-4" />
                    <input
                      id="signup-lastName"
                      name="lastName"
                      type="text"
                      required
                      className="w-full pl-10 pr-3 py-2.5 border border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-neutral-900 text-white placeholder-neutral-500 focus:bg-neutral-900 text-sm"
                      placeholder="Last name"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor="signup-email"
                  className="block text-sm font-medium text-neutral-300 mb-1"
                >
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 w-4 h-4" />
                  <input
                    id="signup-email"
                    name="email"
                    type="email"
                    required
                    className="w-full pl-10 pr-3 py-2.5 border border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-neutral-900 text-white placeholder-neutral-500 focus:bg-neutral-900 text-sm"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="signup-phone"
                  className="block text-sm font-medium text-neutral-300 mb-1"
                >
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 w-4 h-4" />
                  <input
                    id="signup-phone"
                    name="phone"
                    type="tel"
                    required
                    className="w-full pl-10 pr-3 py-2.5 border border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-neutral-900 text-white placeholder-neutral-500 focus:bg-neutral-900 text-sm"
                    placeholder="Enter your phone"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="signup-password"
                  className="block text-sm font-medium text-neutral-300 mb-1"
                >
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 w-4 h-4" />
                  <input
                    id="signup-password"
                    name="password"
                    type={showSignUpPassword ? "text" : "password"}
                    required
                    className="w-full pl-10 pr-10 py-2.5 border border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-neutral-900 text-white placeholder-neutral-500 focus:bg-neutral-900 text-sm"
                    placeholder="Create password"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowSignUpPassword(!showSignUpPassword);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
                  >
                    {/* {showSignUpPassword ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )} */}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="signup-confirmPassword"
                  className="block text-sm font-medium text-neutral-300 mb-1"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 w-4 h-4" />
                  <input
                    id="signup-confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    className="w-full pl-10 pr-10 py-2.5 border border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-neutral-900 text-white placeholder-neutral-500 focus:bg-neutral-900 text-sm"
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowConfirmPassword(!showConfirmPassword);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
                  >
                    {/* {showConfirmPassword ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )} */}
                  </button>
                </div>
              </div>

              <div className="flex flex-col items-start space-y-2">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    required
                    className="w-4 h-4 text-primary border-neutral-700 rounded focus:ring-primary bg-neutral-900 mt-1"
                  />
                  <label className="ml-2 text-sm text-neutral-400">
                    I agree to the{" "}
                    <button
                      type="button"
                      className="text-primary hover:text-primary-light font-medium underline"
                      onClick={() => setTermsOpen(true)}
                    >
                      Terms of Service
                    </button>{" "}
                    and{" "}
                    <button
                      type="button"
                      className="text-primary hover:text-primary-light font-medium underline"
                      onClick={() => setPrivacyOpen(true)}
                    >
                      Privacy Policy
                    </button>
                  </label>
                </div>

                <Modal
                  isOpen={isTermsOpen}
                  onClose={() => setTermsOpen(false)}
                  title="Terms of Service"
                >
                  <p>
                    {/* Terms content */}
                    Welcome to our website. By using this service, you agree to
                    the following terms...
                  </p>
                </Modal>

                <Modal
                  isOpen={isPrivacyOpen}
                  onClose={() => setPrivacyOpen(false)}
                  title="Privacy Policy"
                >
                  <p>
                    {/* Privacy content */}
                    Your privacy is important to us. We collect data to improve
                    our services...
                  </p>
                </Modal>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-linear-to-r from-primary to-primary-dark text-black py-3 rounded-lg font-semibold hover:from-primary-light hover:to-primary focus:ring-4 focus:ring-primary/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-neutral-400">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => setCurrentPage("signin")}
                className="text-primary hover:text-primary-light font-medium"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return currentPage === "signin" ? <SignInPage /> : <SignUpPage />;
};

export default Auth;
